import { Link, router, usePage } from "@inertiajs/react"
import { Building2, Check, ChevronsUpDown, Settings } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import type { Organization } from "@/types"

export function OrgSwitcher() {
  const { t } = useTranslation()
  const { current_organization, organizations } = usePage().props
  const [open, setOpen] = useState(false)

  const currentOrg = current_organization as Organization | null
  const orgList = (organizations as Organization[]) || []

  const handleSwitch = (org: Organization) => {
    if (org.id === currentOrg?.id) return
    router.patch(
      `/organizations/${org.id}/switch`,
      {},
      {
        preserveScroll: false,
      },
    )
    setOpen(false)
  }

  const getInitial = (name: string) => name.charAt(0).toUpperCase()

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="bg-primary text-primary-foreground flex aspect-square size-8 items-center justify-center rounded-full text-sm font-bold">
                {currentOrg?.logo_url ? (
                  <img
                    src={currentOrg.logo_url}
                    alt={currentOrg.name}
                    className="size-8 rounded-full object-cover"
                  />
                ) : (
                  <span>
                    {currentOrg ? (
                      getInitial(currentOrg.name)
                    ) : (
                      <Building2 className="size-4" />
                    )}
                  </span>
                )}
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {currentOrg?.name ?? t("org_switcher.no_organization")}
                </span>
                <span className="text-muted-foreground truncate text-xs">
                  {currentOrg?.slug ?? ""}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            align="start"
            side="bottom"
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              {t("org_switcher.organizations")}
            </DropdownMenuLabel>
            {orgList.map((org) => (
              <DropdownMenuItem
                key={org.id}
                onClick={() => handleSwitch(org)}
                className="cursor-pointer gap-2 p-2"
              >
                <div className="bg-primary text-primary-foreground flex size-6 items-center justify-center rounded-full border text-xs font-bold">
                  {org.logo_url ? (
                    <img
                      src={org.logo_url}
                      alt={org.name}
                      className="size-6 rounded-full object-cover"
                    />
                  ) : (
                    getInitial(org.name)
                  )}
                </div>
                <span className="flex-1 truncate">{org.name}</span>
                {org.id === currentOrg?.id && (
                  <Check className="text-primary size-4" />
                )}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            {currentOrg && orgList.length > 0 && (
              <DropdownMenuItem className="cursor-pointer gap-2 p-2" asChild>
                <Link href={`/organizations/${currentOrg.id}`}>
                  <div className="bg-background flex size-6 items-center justify-center rounded-md border">
                    <Settings className="size-4" />
                  </div>
                  <span className="text-muted-foreground font-medium">
                    {t("org_switcher.configure_organization")}
                  </span>
                </Link>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
